import React, { useEffect, useState } from "react";
import { Gamepad2 } from "lucide-react";

import NumaLogo from "@/components/NumaLogo";

/**
 * Che cosa si vede mentre la revisione lavora.
 *
 * Due cose, in quest'ordine. A che punto è - «12 di 26 sezioni esaminate», un
 * numero che sale, non un orologio che scorre. E, se l'attesa si allunga, il
 * modo di aprire il passatempo: la finestra del gioco vive fuori di qui,
 * perché deve restare aperta anche quando la revisione finisce - chiuderla di
 * scatto a metà partita sarebbe peggio che non offrirla.
 */

// Quanto si aspetta prima di mostrare l'invito: sotto questa soglia la
// revisione fa in tempo a finire, e il pulsante sarebbe solo un lampo.
const SOGLIA_MS = 6000;

export default function AttesaRevisione({ avanzamento, conAi = false, secondi = 0, onGioca }) {
  const [offribile, setOffribile] = useState(false);

  useEffect(() => {
    const quando = setTimeout(() => setOffribile(true), SOGLIA_MS);
    return () => clearTimeout(quando);
  }, []);

  const fasi = {
    controlli: "Controlli automatici",
    ortografia: "Ortografia",
    modello: "Lettura del modello",
    correzioni: "Correzioni applicate al testo",
    meccaniche: "Correzioni meccaniche",
  };
  const correzioni = avanzamento?.fase === "correzioni" || avanzamento?.fase === "meccaniche";
  const fase = fasi[avanzamento?.fase] || (conAi ? "Lettura in corso" : "Controlli in corso");
  const totale = Number(avanzamento?.totale) || 0;
  const fatte = Math.min(Number(avanzamento?.fatte) || 0, totale);
  const quota = totale ? Math.round((fatte / totale) * 100) : 0;

  return (
    <div className="mt-6 space-y-3" data-testid="review-waiting">
      <div className="rounded-md border border-line bg-surface px-4 py-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[13.5px] text-ink">{fase}</p>
          <p className="numa-meta tabular-nums" data-testid="review-progress">
            {totale > 1
              ? `${fatte} di ${totale} sezioni ${correzioni ? "corrette" : "esaminate"}`
              : secondi > 3 ? "in corso" : ""}
          </p>
        </div>
        {totale > 1 && (
          <span className="numa-progress-track mt-2 block">
            <span className="numa-progress-fill block transition-all duration-300"
                  style={{ width: `${Math.max(2, quota)}%` }} />
          </span>
        )}
      </div>

      {offribile ? (
        /*
          L'invito si deve vedere.

          Era un bottoncino grigio sotto la barra: chi guardava l'avanzamento
          non lo notava nemmeno. Un'attesa di minuti merita un invito che si
          veda - la mascotte, il colore del marchio, tutta la larghezza - e che
          resti comunque un invito: lo si ignora senza che dia fastidio.
        */
        <button
          type="button"
          data-testid="apri-gioco"
          onClick={onGioca}
          className="flex w-full items-center gap-3 rounded-md border border-brand/30 bg-brand-soft px-4 py-3 text-left transition-colors hover:border-brand/60"
        >
          <NumaLogo size={9} className="numa-mark shrink-0" variant={5} />
          <span className="min-w-0 flex-1">
            <span className="block text-[14px] font-medium text-brand">
              Mentre aspetti, gioca con NUMA
            </span>
            <span className="numa-meta block">
              Corri e salta le pile di libri. La revisione va avanti per conto suo.
            </span>
          </span>
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-medium text-on-solid">
            <Gamepad2 className="h-3.5 w-3.5" /> Gioca
          </span>
        </button>
      ) : (
        <div className="numa-shimmer h-12 rounded-md" />
      )}

    </div>
  );
}
